import { useState, useEffect } from 'react'
import { ExpandableConfig, TableData } from '../types'

interface UseTableExpansionOptions<T extends TableData> {
  data: T[]
  keyField: keyof T
  expandable?: ExpandableConfig<T>
  currentPage?: number
}

export const useTableExpansion = <T extends TableData>({
  data,
  keyField,
  expandable,
  currentPage = 0
}: UseTableExpansionOptions<T>) => {
  // Estado para controlar las filas expandidas
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  )
  const [allRowsExpanded, setAllRowsExpanded] = useState<boolean>(false)

  // Función helper para crear IDs únicos
  const createUniqueId = (rowData: T, index: number): string | number => {
    if (
      keyField &&
      rowData[keyField] !== undefined &&
      rowData[keyField] !== null
    ) {
      return `${rowData[keyField]}-${index}`
    }
    return `row-${index}`
  }

  // Función helper para verificar si una fila tiene detalles disponibles
  const hasDetails = (rowData: T): boolean => {
    if (!expandable?.getDetailData) return false

    const detailData = expandable.getDetailData(rowData)

    // Verificar si hay datos de detalle
    if (!detailData || detailData === null) return false

    // Si es un array, verificar que no esté vacío
    if (Array.isArray(detailData)) {
      return detailData.length > 0
    }

    // Si es un objeto, verificar que no esté vacío
    if (typeof detailData === 'object') {
      return Object.keys(detailData).length > 0
    }

    // Para otros tipos de datos, considerar que hay detalles si no es null/undefined
    return true
  }

  // Función para verificar si una fila está expandida
  const isRowExpanded = (id: string | number) => {
    return expandedRows.has(id)
  }

  // Función para expandir/contraer una fila individual
  const toggleRowExpansion = (id: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }

      // Actualizar el estado de todas las filas expandidas
      const rowsWithDetails = data.filter(hasDetails).length
      const expandedRowsCount = newSet.size
      setAllRowsExpanded(
        expandedRowsCount > 0 && expandedRowsCount === rowsWithDetails
      )

      return newSet
    })
  }

  // Función para expandir/contraer todas las filas
  const toggleAllRows = () => {
    if (allRowsExpanded) {
      // Contraer todas las filas
      setExpandedRows(new Set())
      setAllRowsExpanded(false)
    } else {
      // Expandir solo las filas que tienen detalles
      const newExpandedRows = new Set<string | number>()
      data.forEach((rowData, index) => {
        if (hasDetails(rowData)) {
          const rowId = createUniqueId(rowData, index)
          newExpandedRows.add(rowId)
        }
      })
      setExpandedRows(newExpandedRows)
      setAllRowsExpanded(true)
    }
  }

  // Función para expandir todas las filas
  const expandAll = () => {
    const newExpandedRows = new Set<string | number>()
    data.forEach((rowData, index) => {
      if (hasDetails(rowData)) {
        const rowId = createUniqueId(rowData, index)
        newExpandedRows.add(rowId)
      }
    })
    setExpandedRows(newExpandedRows)
    setAllRowsExpanded(true)
  }

  // Función para contraer todas las filas
  const collapseAll = () => {
    setExpandedRows(new Set())
    setAllRowsExpanded(false)
  }

  // Función para expandir una fila específica
  const expandRow = (id: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      newSet.add(id)

      // Actualizar estado de todas expandidas
      const rowsWithDetails = data.filter(hasDetails).length
      setAllRowsExpanded(newSet.size === rowsWithDetails)

      return newSet
    })
  }

  // Función para contraer una fila específica
  const collapseRow = (id: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      newSet.delete(id)
      setAllRowsExpanded(false)
      return newSet
    })
  }

  // Obtener datos de detalle para una fila
  const getDetailData = (row: T) => {
    return expandable?.getDetailData?.(row)
  }

  // Efecto para contraer todas las filas cuando cambia la página o datos
  useEffect(() => {
    if (!expandable?.expandAllByDefault) {
      setExpandedRows(new Set())
      setAllRowsExpanded(false)
    }
  }, [currentPage, data.length, expandable?.expandAllByDefault])

  // Efecto para expansión inicial
  useEffect(() => {
    if (expandable?.expandAllByDefault) {
      expandAll()
    } else if (expandable?.initiallyExpanded) {
      // Expandir filas que tengan datos inicialmente
      const newExpandedRows = new Set<string | number>()
      data.forEach((rowData, index) => {
        if (hasDetails(rowData)) {
          const rowId = createUniqueId(rowData, index)
          newExpandedRows.add(rowId)
        }
      })
      setExpandedRows(newExpandedRows)
    }
  }, [data])

  // Estadísticas útiles
  const stats = {
    totalRows: data.length,
    rowsWithDetails: data.filter(hasDetails).length,
    expandedRowsCount: expandedRows.size,
    allRowsExpanded: allRowsExpanded,
    hasExpandableRows: data.some(hasDetails)
  }

  return {
    // Estado
    expandedRows,
    allRowsExpanded,

    // Funciones principales
    toggleRowExpansion,
    toggleAllRows,
    expandAll,
    collapseAll,
    expandRow,
    collapseRow,

    // Utilidades
    isRowExpanded,
    hasDetails,
    createUniqueId,
    getDetailData,

    // Estadísticas
    stats
  }
}
